// @tennis-matches/ui/tournament-setup/PlayerInput
// Generic Player Input Component for Tournament Setup

import React, { useCallback, useMemo } from "react";
import { usePlayerInput } from "./usePlayerInput.js";
import { PlayerAutocomplete } from "./PlayerAutocomplete.jsx";
import { usePlayerAutocomplete } from "./usePlayerAutocomplete.js";
import { __asStr } from "@tennis-matches/core/utils";
import { findDuplicateFields } from "./playerInputUtils.js";

const LK_MIN = 1;
const LK_MAX = 25;

function parseLkValue(str) {
  if (str == null || typeof str !== "string") return NaN;
  const normalized = __asStr(str).trim().replace(",", ".");
  if (normalized === "") return NaN;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
}

function clampLk(n) {
  if (!Number.isFinite(n)) return "";
  const clamped = Math.max(LK_MIN, Math.min(LK_MAX, n));
  return String(clamped);
}

/**
 * PlayerInput Component
 * Generic player name input component for tournament setup wizards
 *
 * @param {Object} props
 * @param {Object} props.names - External names object (controlled)
 * @param {Function} props.onNamesChange - Callback when names change (names, count)
 * @param {number} props.minFields - Minimum number of fields to show
 * @param {string} props.format - Tournament format (singles/doubles) - for optional field calculation
 * @param {number} props.minRequired - Minimum required players (for optional field calculation)
 * @param {boolean} props.showLkField - When true, show LK (Leistungsklasse) input per player (e.g. for LK-basiert team allocation)
 * @param {Object} [props.lkValues] - Optional controlled LK values { "1": "7", "2": "5", ... }
 * @param {Function} [props.onLkChange] - Optional callback when LK values change (lkByPlayer)
 * @param {string} props.className - Additional CSS class for container
 * @param {string} props.inputClassName - Additional CSS class for inputs
 * @param {Function} props.t - Translation function (optional)
 * @param {string} props.hintText - Hint text to display (optional)
 * @param {boolean} props.showPlayerCount - Whether to show player count display (default: true)
 * @param {Function} props.onSearchPlayers - Callback for searching players (async, returns Promise<Array>)
 * @param {Function} props.onPlayerSave - Callback when player is confirmed (on blur)
 * @param {Function} props.onPlayerSelect - Callback when a suggested player/contact is selected
 * @param {Function} props.onPlayerDelete - Callback when player is deleted (playerId) => Promise<void>
 * @param {boolean} [props.autoSaveOnBlur] - When true, calls onPlayerSave for manually typed names on blur
 * @param {boolean} [props.showExcludeUI] - When true, show Ban button and expandable exclusion panel per row
 * @param {Object} [props.excludesByPlayer] - { "1": [2, 4], "2": [] } – player key → excluded player indices
 * @param {Function} [props.onExcludeChange] - (playerKey, excludes) => void
 * @param {string|null} [props.showExcludesKey] - Which row's exclusion panel is open (player key or null)
 * @param {Function} [props.onShowExcludesChange] - (key) => void
 * @param {React.ReactNode} [props.excludeButtonIcon] - Icon for the exclusion button (e.g. <Ban />)
 * @param {boolean} [props.showAddPlayerButton] - When true, show "+ Weiteren Spieler hinzufügen" at the bottom
 * @param {boolean} [props.showHintBox] - When false, do not render the internal tip box (e.g. when parent shows v0 hint boxes)
 */
export function PlayerInput({
  names: externalNames,
  onNamesChange,
  minFields = 4,
  format = "singles",
  minRequired = null,
  showLkField = false,
  lkValues: externalLkValues = null,
  onLkChange = null,
  className = "",
  inputClassName = "",
  t = null,
  hintText = null,
  showPlayerCount = true,
  onSearchPlayers = null,
  onPlayerSave = null,
  onPlayerSelect = null,
  onPlayerDelete = null,
  autoSaveOnBlur = false,
  showExcludeUI = false,
  excludesByPlayer = null,
  onExcludeChange = null,
  showExcludesKey = null,
  onShowExcludesChange = null,
  excludeButtonIcon = null,
  showAddPlayerButton = false,
  showHintBox = true,
}) {
  // Calculate minRequired from format if not provided
  const calculatedMinRequired = minRequired ?? (format === "singles" ? 3 : 4);

  // Memoize the onChange handler to prevent infinite loops
  const handleNamesChangeStable = useCallback(
    (updatedNames, count) => {
      if (onNamesChange) {
        onNamesChange(updatedNames, count);
      }
    },
    [onNamesChange],
  );

  const {
    names,
    playerCount,
    handleNameChange,
    handleNameBlur,
    handleDirectUpdate,
    addFieldIfNeeded,
  } = usePlayerInput({
    initialNames: externalNames,
    minFields,
    onChange: handleNamesChangeStable,
  });

  // LK (Leistungsklasse) per player: controlled via lkValues/onLkChange or internal state
  const [internalLk, setInternalLk] = React.useState({});
  const lkValues = externalLkValues != null ? externalLkValues : internalLk;

  // Draft LK while typing – commit only on blur/Enter so parent sort runs once per field
  const [draftLkByPlayer, setDraftLkByPlayer] = React.useState({});
  const prevExternalLkRef = React.useRef(externalLkValues);
  React.useEffect(() => {
    if (externalLkValues == null) return;
    try {
      if (
        JSON.stringify(prevExternalLkRef.current) ===
        JSON.stringify(externalLkValues)
      ) {
        return;
      }
    } catch {
      /* ignore */
    }
    prevExternalLkRef.current = externalLkValues;
    setDraftLkByPlayer({});
  }, [externalLkValues]);

  const handleLkChange = useCallback((idx, value) => {
    const key = String(idx);
    setDraftLkByPlayer((prev) => ({ ...prev, [key]: __asStr(value).trim() }));
  }, []);

  const handleLkBlur = useCallback(
    (idx) => {
      const key = String(idx);
      const raw =
        draftLkByPlayer[key] !== undefined
          ? draftLkByPlayer[key]
          : (lkValues || {})[key];
      setDraftLkByPlayer((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const n = parseLkValue(raw);
      const clamped = Number.isFinite(n) ? clampLk(n) : "";
      const prevVal = __asStr((lkValues || {})[key]);
      const nextVal = __asStr(clamped);
      if (prevVal === nextVal) {
        setDraftLkByPlayer((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }
      const next = { ...(lkValues || {}), [key]: clamped };
      if (onLkChange) onLkChange(next);
      else setInternalLk(next);
    },
    [lkValues, onLkChange, draftLkByPlayer],
  );

  // Calculate fields to render: minFields, or one more than last filled, or max key (so "Weiteren Spieler hinzufügen" adds a visible row)
  const fieldsToRender = React.useMemo(() => {
    const entries = Object.entries(names || {});
    const filledIndices = entries
      .filter(([, v]) => {
        const val = __asStr(v).trim();
        return val.length > 0 && val !== "(Optional)" && val !== "(optional)";
      })
      .map(([k]) => Number(k))
      .filter((n) => !isNaN(n) && n > 0);
    const allIndices = entries
      .map(([k]) => Number(k))
      .filter((n) => !isNaN(n) && n > 0);
    const lastFilledIndex =
      filledIndices.length > 0 ? Math.max(...filledIndices) : 0;
    const maxKey = allIndices.length > 0 ? Math.max(...allIndices) : 0;
    return Math.max(minFields, lastFilledIndex + 1, playerCount + 1, maxKey);
  }, [names, minFields, playerCount]);

  // Find fields with duplicate names (for red highlighting)
  const duplicateFields = React.useMemo(() => {
    return findDuplicateFields(names);
  }, [names]);

  // Filled player count (for exclusion UI: show Ban only when > 2)
  const filledPlayersCount = React.useMemo(() => {
    if (!names || typeof names !== "object") return 0;
    return Object.values(names).filter((v) => {
      const n = __asStr(v).trim();
      return n.length > 0 && n !== "(Optional)" && n !== "(optional)";
    }).length;
  }, [names]);

  const excludeList = excludesByPlayer != null ? excludesByPlayer : {};
  const handleToggleExclude = useCallback(
    (playerKey, otherIndex) => {
      if (!onExcludeChange) return;
      const current = excludeList[playerKey] || [];
      const next = current.includes(otherIndex)
        ? current.filter((e) => e !== otherIndex)
        : [...current, otherIndex];
      onExcludeChange(playerKey, next);
    },
    [excludeList, onExcludeChange],
  );
  const otherPlayerIndices = React.useMemo(() => {
    if (!names || typeof names !== "object") return [];
    return Object.entries(names)
      .filter(([, v]) => {
        const n = __asStr(v).trim();
        return n.length > 0 && n !== "(Optional)" && n !== "(optional)";
      })
      .map(([k]) => Number(k))
      .filter((n) => !isNaN(n) && n > 0)
      .sort((a, b) => a - b);
  }, [names]);

  // Autocomplete hook (only if onSearchPlayers is provided)
  const { suggestions, handleSearch } = usePlayerAutocomplete({
    onSearch: onSearchPlayers || (() => Promise.resolve([])),
  });

  // Determine if a field is required
  const isFieldRequired = (idx) => {
    return idx <= calculatedMinRequired;
  };

  // Translation helper
  const translate = (key, fallback) => {
    if (t && typeof t === "function") {
      const translated = t(key);
      return translated && translated !== key ? translated : fallback;
    }
    return fallback;
  };

  // Handle player blur with save
  // skipAutoFill: true = don't run auto-fill (e.g., when selecting from autocomplete)
  // skipAutoFill: false = run auto-fill (e.g., when manually typing and blurring)
  const handlePlayerBlur = useCallback(
    (idx, e, skipAutoFill = false) => {
      const playerName = __asStr(names[String(idx)]);

      if (skipAutoFill) {
        // When autocomplete is selected, just update parent with current state (no auto-fill)
        handleDirectUpdate();
      } else {
        // Normal blur: run auto-fill
        handleNameBlur();
      }

      if (
        autoSaveOnBlur &&
        !skipAutoFill &&
        playerName &&
        playerName !== "(Optional)" &&
        playerName !== "(optional)"
      ) {
        onPlayerSave?.(playerName, {
          index: idx,
          setName: (nextName) => handleNameChange(String(idx), nextName),
        });
      }
    },
    [
      autoSaveOnBlur,
      handleNameBlur,
      handleDirectUpdate,
      handleNameChange,
      names,
      onPlayerSave,
    ],
  );

  return (
    <div className={className} style={{ width: "100%" }}>
      {/* Prominent player count box - centered like other wizard pages */}
      {showPlayerCount && (
        <div
          className="field field--full"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "2rem",
            padding: "1.5rem",
            backgroundColor: "#f9fafb",
            borderRadius: "0.75rem",
            border: "2px solid #e5e7eb",
            gridColumn: "1 / -1",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "1rem 1.5rem",
              backgroundColor: "#ffffff",
              borderRadius: "0.5rem",
              border: "2px solid #3b82f6",
              minWidth: "100px",
            }}
          >
            <span
              style={{
                fontSize: "0.75rem",
                fontWeight: "500",
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: "0.25rem",
              }}
            >
              {translate("settings.playersCount", "Spieler")}
            </span>
            <span
              style={{
                fontSize: "2.5rem",
                fontWeight: "bold",
                color: "#3b82f6",
                lineHeight: "1",
              }}
            >
              {playerCount}
            </span>
          </div>
        </div>
      )}

      {/* Player input grid */}
      <div
        className={`playersGrid ${fieldsToRender <= 3 ? "playersGrid--two-cols" : ""}`}
        data-slot="playersGrid"
        style={{ width: "100%" }}
      >
        {Array.from({ length: fieldsToRender }, (_, i) => i + 1).map((idx) => {
          const name = __asStr(names?.[String(idx)] || "");
          const isFilled =
            name.length > 0 && name !== "(Optional)" && name !== "(optional)";
          const isRequired = isFieldRequired(idx);
          const isDuplicate = duplicateFields.has(String(idx));
          const label = translate(
            `settings.playerNameLabel`,
            `Spieler ${idx}`,
          ).replace("{n}", String(idx));

          const handleFocus = (e) => {
            const input = e.target;
            input.removeAttribute("name");
            input.setAttribute("name", "");
            input.setAttribute("autocomplete", "url");
            input.setAttribute("inputMode", "url");
            input.setAttribute("type", "url");
            input.setAttribute("form", "");
            input.setAttribute("readonly", "readonly");
            requestAnimationFrame(() => {
              setTimeout(() => {
                if (input === document.activeElement) {
                  input.removeAttribute("readonly");
                  input.focus();
                }
              }, 10);
            });
            input.style.caretColor = "#000000";
            /* Kein blauer Rahmen am Input – Zustand zeigt die umgebende Namensbox (z. B. Wizard: grün bei ausgefüllt) */
          };

          const handleChange = (e) => {
            const input = e.target;
            if (input.getAttribute("inputMode") !== "url") {
              input.setAttribute("inputMode", "url");
            }
            if (input.getAttribute("type") !== "url") {
              input.setAttribute("type", "url");
            }
            if (input.hasAttribute("readonly")) {
              input.removeAttribute("readonly");
              if (input._focusTimeout) {
                clearTimeout(input._focusTimeout);
                input._focusTimeout = null;
              }
            }
            handleNameChange(String(idx), e.target.value);
          };

          const lkValue =
            draftLkByPlayer[String(idx)] !== undefined
              ? draftLkByPlayer[String(idx)]
              : lkValues && lkValues[String(idx)]
                ? lkValues[String(idx)]
                : "";
          const lkNum = parseLkValue(lkValue);
          const isLkInvalid =
            Number.isFinite(lkNum) && (lkNum < LK_MIN || lkNum > LK_MAX);

          const rowClass = [
            "player-input-row",
            showLkField ? "player-input-row--with-lk" : "",
            isFilled ? "player-input-row--filled" : "",
            showExcludeUI ? "player-input-row--with-exclude" : "",
            isDuplicate ? "player-input-row--error" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const keyStr = String(idx);
          const excludes = excludeList[keyStr] || [];
          const showExcludesPanel = showExcludesKey === keyStr;
          const topStyle =
            showLkField || showExcludeUI
              ? {
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                }
              : {};
          const rowStyle = showExcludeUI
            ? { display: "flex", flexDirection: "column", gap: "0.5rem" }
            : showLkField
              ? {
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                }
              : {};
          return (
            <div key={idx} className={rowClass} style={rowStyle}>
              <div className="player-input-row__top" style={topStyle}>
                <div
                  style={{
                    flex: showLkField || showExcludeUI ? 1 : undefined,
                    minWidth: 0,
                    width: showLkField || showExcludeUI ? undefined : "100%",
                  }}
                >
                  <label
                    htmlFor={`player-${idx}`}
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontSize: "0.875rem",
                      color: isRequired ? "#374151" : "#9ca3af",
                      fontWeight: isRequired ? "500" : "normal",
                    }}
                  >
                    {label}
                    {isRequired && (
                      <span style={{ color: "#ef4444", marginLeft: "0.25rem" }}>
                        *
                      </span>
                    )}
                  </label>
                  {onSearchPlayers ? (
                    <div style={{ width: "100%" }}>
                      <PlayerAutocomplete
                        inputId={`player-${idx}`}
                        optionalField={!isRequired}
                        value={name}
                        onChange={(value) =>
                          handleNameChange(String(idx), value)
                        }
                        onSelect={(suggestion) => {
                          if (suggestion && suggestion.name) {
                            handleNameChange(String(idx), suggestion.name);
                            onPlayerSelect?.(suggestion, { index: idx });
                            setTimeout(
                              () => handlePlayerBlur(idx, null, true),
                              50,
                            );
                          }
                        }}
                        onDelete={onPlayerDelete}
                        onBlur={(e) => handlePlayerBlur(idx, e, false)}
                        onSearch={handleSearch}
                        suggestions={suggestions}
                        placeholder={
                          isRequired
                            ? translate(
                                "settings.playerNamePlaceholder",
                                "Name eingeben oder aus Liste wählen",
                              )
                            : translate(
                                "settings.playerNameOptional",
                                "Optional",
                              )
                        }
                        isRequired={isRequired}
                        className={inputClassName}
                        style={{
                          width: "100%",
                          border: isFilled
                            ? "1px solid transparent"
                            : undefined,
                          backgroundColor: "transparent",
                          caretColor: undefined,
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{ position: "relative", width: "100%" }}>
                      <input
                        type="url"
                        id={`player-${idx}`}
                        value={name}
                        onBlur={(e) => handlePlayerBlur(idx, e, false)}
                        placeholder={
                          isRequired
                            ? translate(
                                "settings.playerNamePlaceholder",
                                "Name eingeben oder aus Liste wählen",
                              )
                            : translate(
                                "settings.playerNameOptional",
                                "Optional",
                              )
                        }
                        // NUCLEAR OPTION: Use type="url" - URLs are NOT linked to address book in iOS
                        // This is the most effective way to prevent address book suggestions
                        name=""
                        form=""
                        // ULTRA-AGGRESSIVE autocomplete prevention for Safari/iOS
                        // Use "url" autocomplete - URLs are NOT linked to address book
                        autoComplete="url"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        inputMode="url"
                        // Safari/iOS specific (as data attributes since they're non-standard)
                        data-x-autocomplete="off"
                        data-webkit-autocomplete="off"
                        // Password manager / LastPass / 1Password prevention
                        data-lpignore="true"
                        data-form-type="other"
                        data-1p-ignore="true"
                        data-bwignore="true"
                        // Additional iOS/Safari prevention
                        data-safari-autocomplete="off"
                        data-ios-autocomplete="off"
                        data-addressbook="off"
                        data-autocomplete="off"
                        data-contacts="off"
                        // ARIA attributes
                        role="textbox"
                        aria-autocomplete="list"
                        aria-label={
                          isRequired
                            ? translate(
                                "settings.playerNamePlaceholder",
                                "Name eingeben oder aus Liste wählen",
                              )
                            : translate(
                                "settings.playerNameOptional",
                                "Optional",
                              )
                        }
                        onFocus={handleFocus}
                        onKeyDown={(e) => {
                          const input = e.target;
                          if (input.getAttribute("inputMode") !== "url") {
                            input.setAttribute("inputMode", "url");
                          }
                          if (input.getAttribute("type") !== "url") {
                            input.setAttribute("type", "url");
                          }
                        }}
                        onChange={handleChange}
                        className={inputClassName}
                        data-player={String(idx)}
                        data-optional={!isRequired}
                        maxLength={20}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          paddingRight: isFilled ? "2.5rem" : "0.75rem", // Make room for clear button
                          border: isFilled
                            ? "1px solid transparent"
                            : "1px solid #d1d5db",
                          borderRadius: "0.5rem",
                          fontSize: "1rem",
                          transition: "all 0.15s ease",
                          backgroundColor: isFilled ? "transparent" : "#ffffff",
                          outline: "none",
                          // Additional Safari-specific CSS
                          WebkitAppearance: "none",
                          WebkitUserSelect: "text",
                          // Prevent iOS from showing address book suggestions
                          WebkitTapHighlightColor: "transparent",
                          // Ensure cursor is always visible - use black for maximum contrast
                          // Even for duplicates, use black cursor for visibility (border shows the error state)
                          caretColor: "#000000",
                        }}
                      />

                      {/* Clear button (X) - only visible when field has content */}
                      {isFilled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleNameChange(String(idx), "");
                            const input = document.getElementById(
                              `player-${idx}`,
                            );
                            if (input) {
                              input.focus();
                            }
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevent input blur
                            e.stopPropagation();
                          }}
                          style={{
                            position: "absolute",
                            right: "0.5rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "0.25rem",
                            borderRadius: "0.25rem",
                            color: "#6b7280",
                            fontSize: "1.25rem",
                            fontWeight: "bold",
                            lineHeight: "1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "24px",
                            height: "24px",
                            transition: "all 0.15s ease",
                            opacity: 0.7,
                            zIndex: 10,
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.opacity = "1";
                            e.target.style.backgroundColor = "#f3f4f6";
                            e.target.style.color = "#111827";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.opacity = "0.7";
                            e.target.style.backgroundColor = "transparent";
                            e.target.style.color = "#6b7280";
                          }}
                          aria-label="Feld leeren"
                          title="Feld leeren"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {showLkField && (
                  <div
                    className="player-input-row__lk"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      flexShrink: 0,
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span
                      className="player-input-row__lk-label"
                      style={{
                        fontSize: "0.875rem",
                        color: "#6b7280",
                        fontWeight: 500,
                      }}
                    >
                      {translate("settings.wizard.lkShort", "LK")}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`player-input-row__lk-input ${inputClassName} ${isLkInvalid ? "player-input-row__lk-input--invalid" : ""}`}
                      value={lkValue}
                      onChange={(e) => handleLkChange(idx, e.target.value)}
                      onBlur={() => handleLkBlur(idx)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.target.blur();
                        }
                      }}
                      placeholder="--"
                      aria-label={translate(
                        "settings.wizard.lkForPlayer",
                        "Leistungsklasse Spieler {n}",
                      ).replace("{n}", String(idx))}
                      aria-invalid={isLkInvalid}
                      title={translate("settings.wizard.lkRange", "1,0 – 25,0")}
                      data-player-lk={String(idx)}
                      maxLength={5}
                      style={{
                        width: "4rem",
                        maxWidth: "5rem",
                        padding: "0.5rem 0.5rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.5rem",
                        fontSize: "0.875rem",
                        backgroundColor: "#f9fafb",
                      }}
                    />
                  </div>
                )}
                {showExcludeUI && isFilled && filledPlayersCount > 2 && (
                  <button
                    type="button"
                    className={`player-input-row__ban ${excludes.length > 0 ? "player-input-row__ban--active" : ""}`}
                    onClick={() =>
                      onShowExcludesChange?.(showExcludesPanel ? null : keyStr)
                    }
                    title={translate(
                      "settings.wizard.exclusion.buttonTitle",
                      "Ausschlüsse festlegen",
                    )}
                    aria-label={translate(
                      "settings.wizard.exclusion.buttonTitle",
                      "Ausschlüsse festlegen",
                    )}
                  >
                    {excludeButtonIcon}
                  </button>
                )}
              </div>
              {showExcludesPanel && (
                <div className="player-input-row__excludes">
                  <p className="player-input-row__excludes-label">
                    {translate(
                      "settings.wizard.exclusion.prompt",
                      "Mit wem soll {name} NICHT spielen?",
                    ).replace("{name}", name || "")}
                  </p>
                  <div className="player-input-row__excludes-chips">
                    {otherPlayerIndices
                      .filter((i) => i !== idx)
                      .map((otherIndex) => {
                        const otherName = __asStr(
                          names?.[String(otherIndex)] ?? "",
                        );
                        if (!otherName.trim()) return null;
                        const isExcluded = excludes.includes(otherIndex);
                        return (
                          <button
                            key={otherIndex}
                            type="button"
                            className={`player-input-row__excludes-chip ${isExcluded ? "player-input-row__excludes-chip--excluded" : ""}`}
                            onClick={() =>
                              handleToggleExclude(keyStr, otherIndex)
                            }
                          >
                            {isExcluded && excludeButtonIcon}
                            {otherName}
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* "+ Weiteren Spieler hinzufügen" (v0: am Ende der Liste) */}
      {showAddPlayerButton && (
        <button
          type="button"
          className="player-input-add-player-btn"
          onClick={() => addFieldIfNeeded()}
          aria-label={translate(
            "settings.wizard.addPlayer",
            "Weiteren Spieler hinzufügen",
          )}
        >
          <span className="player-input-add-player-btn__icon" aria-hidden>
            +
          </span>
          <span>
            {translate(
              "settings.wizard.addPlayer",
              "Weiteren Spieler hinzufügen",
            )}
          </span>
        </button>
      )}

      {/* Fehlermeldung bei doppelten Spielernamen */}
      {duplicateFields.size > 0 && (
        <div
          className="tm-wizard-error-box player-input-duplicate-error"
          role="alert"
        >
          <p
            className="tm-wizard-error-box__title player-input-duplicate-error__text"
            style={{ margin: 0 }}
          >
            {translate(
              "settings.validation.namesDuplicate",
              "Ein Spielername kommt 2x oder mehrfach vor.",
            )}
          </p>
        </div>
      )}

      {/* Info text (nur wenn showHintBox; sonst zeigt Parent z. B. v0-Hint-Boxen) */}
      {showHintBox && (
        <div
          style={{
            marginTop: "1.5rem",
            padding: "1rem",
            backgroundColor: "#f0f9ff",
            borderLeft: "4px solid #3b82f6",
            borderRadius: "0.375rem",
          }}
        >
          <p
            style={{
              fontSize: "0.875rem",
              color: "#1e40af",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            <strong>{translate("settings.playerInputTip", "Tipp")}:</strong>{" "}
            {translate(
              "settings.playerInputTipText",
              `Mindestens ${minFields} ${format === "doubles" ? "Doppel-" : ""}Spieler sind üblich. Weitere Felder erscheinen automatisch beim Ausfüllen.`,
            )}
            {onSearchPlayers &&
              ` ${translate("settings.playerInputAutocompleteHint", "Beginne zu tippen, um gespeicherte Spieler vorzuschlagen.")}`}
          </p>
        </div>
      )}
    </div>
  );
}
